<template>
    <div class="remote-users-tab">
        <div class="d-flex justify-end mb-4 remote-actions">
            <v-btn outlined color="primary" small @click="openAddUserDialog" class="mr-2">
                <v-icon left small>mdi-account-plus</v-icon>
                Add User
            </v-btn>
            <v-btn v-if="!currentUserIsRemote" outlined color="primary" small :loading="addingMyself" @click="addMyself">
                <v-icon left small>mdi-account-plus</v-icon>
                Add Myself
            </v-btn>
        </div>

        <div v-for="group in groups" :key="group.unit" class="remote-group mb-6">
            <div class="d-flex align-center flex-wrap remote-group-header mb-2">
                <div v-if="group.color" class="legend-dot mr-2" :style="{ backgroundColor: group.color }"></div>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">{{ group.label }}</h3>
                <v-chip x-small outlined class="ml-2">{{ group.members.length }}</v-chip>
            </div>

            <v-row v-if="group.members.length" dense>
                <v-col v-for="ru in group.members" :key="ru.user.id" cols="12" sm="4">
                    <v-card outlined class="pa-3 remote-user-card" @click="viewUser(ru.user)">
                        <div class="d-flex align-center mb-2">
                            <v-avatar size="64" class="mr-3">
                                <v-img v-if="ru.user.profile_picture" :src="getProfilePictureUrl(ru.user.profile_picture)"></v-img>
                                <v-avatar v-else color="primary" size="64" class="white--text">{{ initials(ru.user) }}</v-avatar>
                            </v-avatar>
                            <div class="overflow-hidden">
                                <div class="text-body-2 font-weight-bold text-truncate">{{ ru.user.name }}</div>
                                <div v-if="detailTitle(ru.user)" class="text-caption grey--text text-truncate">{{ detailTitle(ru.user) }}</div>
                            </div>
                        </div>
                        <div v-if="compactFields(ru.user).length" class="remote-user-details">
                            <div v-for="field in compactFields(ru.user)" :key="field.key" class="text-caption">
                                <strong>{{ field.label }}:</strong> {{ field.value }}
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
            <div v-else class="text-center text--secondary py-4">No remote users yet.</div>
        </div>

        <UserDetailsDialog v-model="showViewDialog" :user="viewingUser">
            <template v-slot:extra-info="{ user }">
                <div v-if="detailFieldsFor(user).length" class="mb-6">
                    <h4 class="text-subtitle-1 font-weight-bold mb-3">Get to Know {{ firstNameOf(user) }}</h4>
                    <v-row dense>
                        <v-col v-for="field in detailFieldsFor(user)" :key="field.key" cols="12" sm="6">
                            <div class="text-caption font-weight-bold grey--text text--darken-1">{{ field.label }}</div>
                            <div class="text-body-2 mb-2">{{ field.value }}</div>
                        </v-col>
                    </v-row>
                </div>
            </template>
            <template v-slot:actions="{ user, close: closeView }">
                <v-btn text color="error" :loading="removing" @click="removeUser(user)">Remove from List</v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeView">Close</v-btn>
                <v-btn v-if="isViewingOwnDetails" color="primary" text @click="editUser(user, closeView)">Edit Details</v-btn>
            </template>
        </UserDetailsDialog>

        <FloorPlanUserDetailsDialog v-model="showFormDialog" :user="formUser"
            :details="formUser ? floorPlanUserDetails()[formUser.id] : null" :saving="savingForm"
            @save="handleSaveDetails" />

        <v-dialog v-model="showAddDialog" max-width="480">
            <v-card>
                <v-card-title class="primary white--text">
                    <span class="text-h6">Add Remote User</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="showAddDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-autocomplete v-model="addUserId" :items="availableUsersFor()" item-text="displayLabel"
                        item-value="id" label="Search for a user" prepend-inner-icon="mdi-account-search" outlined
                        dense clearable autofocus>
                        <template v-slot:item="{ item }">
                            <v-list-item-avatar size="32">
                                <v-img v-if="item.profile_picture" :src="getProfilePictureUrl(item.profile_picture)"></v-img>
                                <v-avatar v-else color="primary" size="32" class="white--text">{{ initials(item) }}</v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ roleLabel(item) }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                    <v-combobox v-model="addUnit" :items="unitOptions" label="Team / Unit (optional)"
                        prepend-inner-icon="mdi-domain" outlined dense clearable hint="Leave blank for No Team"
                        persistent-hint></v-combobox>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="showAddDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :disabled="!addUserId" @click="confirmAddUser">Next</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserDetailsDialog from '../../modules/UserDetailsDialog.vue';
import FloorPlanUserDetailsDialog from './FloorPlanUserDetailsDialog.vue';
import { firstName, detailFields } from '../userDetailFields';
import { getUnit, unitColor, NO_TEAM_UNIT } from '../unitColors';

export default {
    name: 'RemoteUsersTab',
    components: { UserDetailsDialog, FloorPlanUserDetailsDialog },
    inject: [
        'users', 'floorPlanSeats', 'floorPlanRemoteUsers', 'addFloorPlanRemoteUser', 'removeFloorPlanRemoteUser',
        'floorPlanUserDetails', 'refreshFloorPlanUserDetails', 'saveFloorPlanUserDetails',
    ],
    data() {
        return {
            showViewDialog: false,
            viewingUser: null,
            showFormDialog: false,
            formUser: null,
            pendingUnit: null,
            savingForm: false,
            removing: false,
            addingMyself: false,
            showAddDialog: false,
            addUserId: null,
            addUnit: null,
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        existingRemoteUserIds() {
            return new Set((this.floorPlanRemoteUsers() || []).filter((ru) => ru.user).map((ru) => ru.user.id));
        },
        remoteUserIdsKey() {
            return Array.from(this.existingRemoteUserIds).sort((a, b) => a - b).join(',');
        },
        currentUserIsRemote() {
            return !!(this.get_user && this.existingRemoteUserIds.has(this.get_user.id));
        },
        isViewingOwnDetails() {
            return !!(this.viewingUser && this.get_user && this.viewingUser.id === this.get_user.id);
        },
        allUnitGroups() {
            const map = new Map();
            const ensure = (unit) => {
                if (!map.has(unit)) {
                    map.set(unit, { unit, label: unit, color: unitColor(unit), members: [] });
                }
                return map.get(unit);
            };
            (this.floorPlanSeats() || []).forEach((seat) => {
                const unit = getUnit(seat.user);
                if (unit) ensure(unit);
            });
            (this.floorPlanRemoteUsers() || []).forEach((ru) => {
                if (!ru.user) return;
                const unit = ru.unit || NO_TEAM_UNIT;
                ensure(unit).members.push(ru);
            });
            ensure(NO_TEAM_UNIT);
            return map;
        },
        groups() {
            const noTeam = this.allUnitGroups.get(NO_TEAM_UNIT);
            const rest = Array.from(this.allUnitGroups.values())
                .filter((g) => g.unit !== NO_TEAM_UNIT && g.members.length)
                .sort((a, b) => a.label.localeCompare(b.label));
            return noTeam.members.length ? [noTeam, ...rest] : rest;
        },
        unitOptions() {
            return Array.from(this.allUnitGroups.keys())
                .filter((unit) => unit !== NO_TEAM_UNIT)
                .sort((a, b) => a.localeCompare(b));
        },
    },
    watch: {
        remoteUserIdsKey: {
            immediate: true,
            handler() {
                this.loadMissingDetails();
            },
        },
    },
    methods: {
        loadMissingDetails() {
            const existing = this.floorPlanUserDetails();
            this.existingRemoteUserIds.forEach((id) => {
                if (!(id in existing)) {
                    this.refreshFloorPlanUserDetails(id).catch((error) => {
                        console.error('Error fetching remote user details:', error);
                    });
                }
            });
        },
        availableUsersFor() {
            return (this.users() || [])
                .filter((user) => !this.existingRemoteUserIds.has(user.id))
                .map((user) => ({ ...user, displayLabel: `${user.name} (${this.roleLabel(user)})` }));
        },
        roleLabel(user) {
            if (user.role_members && user.role_members.length) {
                return user.role_members.map((m) => m.role && m.role.name).filter(Boolean).join(', ') || 'No Role';
            }
            return (user.role && user.role.name) || 'No Role';
        },
        initials(user) {
            if (!user || !user.name) return '?';
            const names = user.name.split(' ');
            return names.length >= 2 ? (names[0][0] + names[1][0]).toUpperCase() : user.name.substring(0, 2).toUpperCase();
        },
        getProfilePictureUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${this.apiUrl}/${path}`;
        },
        detailTitle(user) {
            const details = this.floorPlanUserDetails()[user.id];
            return details && details.title;
        },
        compactFields(user) {
            const details = this.floorPlanUserDetails()[user.id];
            return detailFields(details, ['start_date', 'from_location', 'company_email', 'what_i_actually_do', 'hobbies', 'ask_me_about', 'fun_fact']);
        },
        detailFieldsFor(user) {
            if (!user) return [];
            return detailFields(this.floorPlanUserDetails()[user.id]);
        },
        firstNameOf(user) {
            return firstName(user);
        },
        openAddUserDialog() {
            this.addUserId = null;
            this.addUnit = null;
            this.showAddDialog = true;
        },
        confirmAddUser() {
            if (!this.addUserId) return;
            const user = (this.users() || []).find((u) => u.id === this.addUserId);
            if (!user) return;
            this.showAddDialog = false;
            this.pendingUnit = this.addUnit || NO_TEAM_UNIT;
            this.formUser = user;
            this.refreshFloorPlanUserDetails(user.id).catch((error) => {
                console.error('Error fetching existing user details:', error);
            }).finally(() => {
                this.showFormDialog = true;
            });
        },
        addMyself() {
            if (!this.get_user) return;
            this.addingMyself = true;
            this.pendingUnit = NO_TEAM_UNIT;
            this.formUser = this.get_user;
            this.refreshFloorPlanUserDetails(this.get_user.id).catch((error) => {
                console.error('Error fetching existing user details:', error);
            }).finally(() => {
                this.addingMyself = false;
                this.showFormDialog = true;
            });
        },
        viewUser(user) {
            this.viewingUser = user;
            this.showViewDialog = true;
            this.refreshFloorPlanUserDetails(user.id).catch((error) => {
                console.error('Error fetching user details:', error);
            });
        },
        editUser(user, closeView) {
            closeView();
            this.formUser = user;
            this.pendingUnit = null;
            this.showFormDialog = true;
        },
        async removeUser(user) {
            if (!user) return;
            this.removing = true;
            try {
                await this.removeFloorPlanRemoteUser(user.id);
                this.showViewDialog = false;
                this.$toast.success('Removed from list');
            } catch (error) {
                console.error('Error removing remote user:', error);
                this.$toast.error('Failed to remove remote user');
            } finally {
                this.removing = false;
            }
        },
        async handleSaveDetails(formData) {
            if (!this.formUser) return;
            this.savingForm = true;
            try {
                await this.saveFloorPlanUserDetails(this.formUser.id, formData);
                const targetUnit = (formData.unit && formData.unit.trim()) || this.pendingUnit || NO_TEAM_UNIT;
                await this.addFloorPlanRemoteUser(targetUnit, this.formUser.id);
                this.showFormDialog = false;
                this.$toast.success('Remote user saved');
            } catch (error) {
                console.error('Error saving remote user:', error);
                this.$toast.error('Failed to save remote user');
            } finally {
                this.savingForm = false;
                this.pendingUnit = null;
            }
        },
    },
};
</script>

<style scoped>
.legend-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
}

.remote-user-card {
    cursor: pointer;
    transition: box-shadow 0.15s ease;
    height: 100%;
}

.remote-user-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.remote-user-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    white-space: normal;
    word-break: break-word;
}
</style>
